/**
 * Shared Filter Components
 * 
 * Consolidated, reusable filter components that can be used across different feature areas
 * to eliminate duplication between drug coverage filters and therapeutic class filters.
 */

import { isEmpty, isNil } from 'lodash';
import { useMemo, useState } from 'react';
import { FaFilePrescription, FaFlagUsa, FaSignature } from 'react-icons/fa6';
import { HiMagnifyingGlass } from 'react-icons/hi2';
import { MdGroups } from 'react-icons/md';

import { useGetPoolsLookups } from '@/api/use-pools';
import { useGetStateLookups } from '@/api/use-states';
import { Checkbox, CheckboxField, CheckboxGroup } from '@/components-new/checkbox';
import { Divider } from '@/components-new/divider';
import { Fieldset, Label, Legend } from '@/components-new/fieldset';
import { IndeterminateToggleButton } from '@/components-new/indeterminate';
import { Input, InputGroup } from '@/components-new/input';
import { displayPdlStatus, PdlStatus } from '@/features/coverage/types/pdl-status';
import { useDebounce } from '@/hooks/use-debounce';
import { CodeBasedLookup } from '@/types/lookup';

// Generic filter context interface
interface FilterContext<T extends CodeBasedLookup[]> {
  value: T | undefined;
  setValue: (value: T) => void;
  removeValue: () => void;
}

/**
 * Generic State Filter Component
 * Can be used by both drug coverage and therapeutic class reports
 */
export const StateFilter = <T extends CodeBasedLookup[]>({ value, setValue, removeValue }: FilterContext<T>) => {
  const {
    data: stateLookups,
    isLoading: isLoadingStateLookups,
  } = useGetStateLookups();

  const [searchValue, setSearchValue] = useState('');
  const search = useDebounce(searchValue, 100);

  const options = useMemo(() => {
    if (isNil(stateLookups)) return [];
    if (isEmpty(search)) return stateLookups;

    return stateLookups.filter(option =>
      option.label.toLowerCase().includes(search.toLowerCase()));
  }, [search, stateLookups]);

  return (
    <div className="flex flex-col gap-2">
      <InputGroup>
        <HiMagnifyingGlass/>
        <Input
          name="search"
          placeholder="Search for state..."
          aria-label="Search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </InputGroup>
      <Fieldset>
        <Legend className="flex items-center justify-between">
          State
          <IndeterminateToggleButton
            options={options}
            selectedOptions={value}
            onChange={(checked) => { isEmpty(checked) ? removeValue() : setValue(checked as T); }}
          />
        </Legend>
        <Divider/>
        <CheckboxGroup role="group" aria-label="State Selection">
          {options.map((option) => (
            <CheckboxField key={option.code}>
              <Checkbox
                name="state"
                checked={!!value?.find(value => value.code === option.code)}
                onChange={(checked) => {
                  setValue(
                    (checked
                      ? [...value ?? [], option]
                      : (value ?? []).filter((value) => value.code !== option.code)) as T
                  );
                }}
              />
              <Label>{option.label}</Label>
            </CheckboxField>
          ))}
          {options.length === 0 && !isLoadingStateLookups && (
            <div className="text-gray-500">
              No states match your search.
            </div>
          )}
          {isLoadingStateLookups && (
            <div className="text-gray-500">
              Loading states...
            </div>
          )}
        </CheckboxGroup>
      </Fieldset>
    </div>
  );
};

/**
 * Generic Pools Filter Component
 * Can be used by both drug coverage and therapeutic class reports
 */
export const PoolsFilter = <T extends CodeBasedLookup[]>({ value, setValue, removeValue }: FilterContext<T>) => {
  const {
    data: poolLookups,
    isLoading: isLoadingPoolLookups,
  } = useGetPoolsLookups();

  const [searchValue, setSearchValue] = useState('');
  const search = useDebounce(searchValue, 100);

  const options = useMemo(() => {
    if (isNil(poolLookups)) return [];
    if (isEmpty(search)) return poolLookups;

    return poolLookups.filter(option =>
      option.label.toLowerCase().includes(search.toLowerCase()));
  }, [search, poolLookups]);

  return (
    <div className="flex flex-col gap-2">
      <InputGroup>
        <HiMagnifyingGlass/>
        <Input
          name="search"
          placeholder="Search for pools..."
          aria-label="Search"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </InputGroup>
      <Fieldset>
        <Legend className="flex items-center justify-between">
          Pool
          <IndeterminateToggleButton
            options={options}
            selectedOptions={value}
            onChange={(checked) => { isEmpty(checked) ? removeValue() : setValue(checked as T); }}
          />
        </Legend>
        <Divider/>
        <CheckboxGroup role="group" aria-label="Pool Selection">
          {options.map((option) => (
            <CheckboxField key={option.code}>
              <Checkbox
                name="pool"
                checked={!!value?.find(value => value.code === option.code)}
                onChange={(checked) => {
                  setValue(
                    (checked
                      ? [...value ?? [], option]
                      : (value ?? []).filter((value) => value.code !== option.code)) as T
                  );
                }}
              />
              <Label>{option.label}</Label>
            </CheckboxField>
          ))}
          {options.length === 0 && !isLoadingPoolLookups && (
            <div className="text-gray-500">
              No pools match your search.
            </div>
          )}
          {isLoadingPoolLookups && (
            <div className="text-gray-500">
              Loading pools...
            </div>
          )}
        </CheckboxGroup>
      </Fieldset>
    </div>
  );
};

// PDL Status options - shared constant
const PDL_STATUS_OPTIONS: CodeBasedLookup[] = [
  { code: PdlStatus.BLANK, label: displayPdlStatus(PdlStatus.BLANK) },
  { code: PdlStatus.PREFERRED, label: displayPdlStatus(PdlStatus.PREFERRED) },
  { code: PdlStatus.NON_PREFERRED, label: displayPdlStatus(PdlStatus.NON_PREFERRED) },
  { code: PdlStatus.NON_PDL, label: displayPdlStatus(PdlStatus.NON_PDL) },
  { code: PdlStatus.NO_STATE_PDL, label: displayPdlStatus(PdlStatus.NO_STATE_PDL) },
  { code: PdlStatus.PREFERRED_NOT_LISTED, label: displayPdlStatus(PdlStatus.PREFERRED_NOT_LISTED) },
  { code: PdlStatus.NON_PREFERRED_NOT_LISTED, label: displayPdlStatus(PdlStatus.NON_PREFERRED_NOT_LISTED) },
];

/**
 * Generic PDL Status Filter Component
 * Can be used by both drug coverage and therapeutic class reports
 */
export const PDLStatusFilter = <T extends CodeBasedLookup[]>({ value, setValue, removeValue }: FilterContext<T>) => {
  return (
    <Fieldset>
      <Legend className="flex items-center justify-between">
        PDL Status
        <IndeterminateToggleButton
          options={PDL_STATUS_OPTIONS}
          selectedOptions={value}
          onChange={(checked) => { isEmpty(checked) ? removeValue() : setValue(checked as T); }}
        />
      </Legend>
      <Divider/>
      <CheckboxGroup role="group" aria-label="PDL Status Selection">
        {PDL_STATUS_OPTIONS.map((option) => (
          <CheckboxField key={option.code}>
            <Checkbox
              name="pdlStatus"
              checked={!!value?.find(value => value.code === option.code)}
              onChange={(checked) => {
                setValue(
                  (checked
                    ? [...value ?? [], option]
                    : (value ?? []).filter((value) => value.code !== option.code)) as T
                );
              }}
            />
            <Label>{option.label}</Label>
          </CheckboxField>
        ))}
      </CheckboxGroup>
    </Fieldset>
  );
};

// PA Type options - shared constant  
const PA_TYPE_OPTIONS: CodeBasedLookup[] = [
  { code: 'GeneralPdl', label: 'General PDL' },
  { code: 'GeneralNonPdl', label: 'General Non-PDL' },
  { code: 'ClassSpecific', label: 'Class-Specific' },
  { code: 'DrugSpecific', label: 'Drug-Specific' },
  { code: 'Oncology', label: 'Oncology' },
];

/**
 * Generic PA Type Filter Component  
 * Can be used by both drug coverage and therapeutic class reports
 */
export const PATypeFilter = <T extends CodeBasedLookup[]>({ value, setValue, removeValue }: FilterContext<T>) => {
  return (
    <Fieldset>
      <Legend className="flex items-center justify-between">
        PA Type
        <IndeterminateToggleButton
          options={PA_TYPE_OPTIONS}
          selectedOptions={value}
          onChange={(checked) => { isEmpty(checked) ? removeValue() : setValue(checked as T); }}
        />
      </Legend>
      <Divider/>
      <CheckboxGroup role="group" aria-label="PA Type Selection">
        {PA_TYPE_OPTIONS.map((option) => (
          <CheckboxField key={option.code}>
            <Checkbox
              name="paType"
              checked={!!value?.find(value => value.code === option.code)}
              onChange={(checked) => {
                setValue(
                  (checked
                    ? [...value ?? [], option]
                    : (value ?? []).filter((value) => value.code !== option.code)) as T
                );
              }}
            />
            <Label>{option.label}</Label>
          </CheckboxField>
        ))}
      </CheckboxGroup>
    </Fieldset>
  );
};

/**
 * Shared filter section icons and labels
 * Provides consistent iconography across different filter implementations
 */
export const SHARED_FILTER_SECTIONS = {
  state: {
    icon: FaFlagUsa,
    label: 'State',
  },
  pool: {
    icon: MdGroups,
    label: 'Pool',
  },
  pdlStatus: {
    icon: FaFilePrescription,
    label: 'PDL Status',
  },
  paType: {
    icon: FaSignature,
    label: 'PA Type',
  },
} as const;

/**
 * Utility function to generate multi-filter labels consistently
 */
export const getMultiFilterLabel = <TItem, >(
  label: string,
  value: TItem[] | undefined,
  getLabel: (item?: TItem) => string
) => {
  const selectedCount = value?.length ?? 0;
  const multipleSelected = selectedCount > 1;
  return multipleSelected ? `${label} (${selectedCount})` : `${getLabel(value?.[0])}`;
};
